import productTemplatesQueries from '@/api/modules/productTemplatesQueries'
import { t } from '@/i18n'
import { notificationService } from '@/services/notificationService'
import { useProductTemplatesStore } from '@/stores/orders/productTemplates'
import { handleApiError } from '@/utils/apiHelpers'

class ProductTemplatesService {
    constructor({ store = useProductTemplatesStore() } = {}) {
        this.store = store
    }

    async fetchProductTemplatesByQueryString(queryString = '') {
        const { store } = this

        store.setProductTemplatesLoading(true)
        try {
            const response = await productTemplatesQueries.fetchProductTemplatesWithQueryString(queryString)
            const { data } = response

            store.setProductTemplates(data.results || [])

            return response
        } catch (error) {
            handleApiError(error, t('orders.productTemplates.service.fetchProductTemplatesError'))
            throw error
        } finally {
            store.setProductTemplatesLoading(false)
        }
    }

    async fetchProductTemplate(productTemplateId) {
        try {
            const response = await productTemplatesQueries.fetchProductTemplate(productTemplateId)
            return response
        } catch (error) {
            handleApiError(error, t('orders.productTemplates.service.fetchProductTemplateError'))
            throw error
        }
    }

    async createProductTemplate(payload) {
        try {
            const response = await productTemplatesQueries.createProductTemplate(payload)
            notificationService.success(t('orders.productTemplates.service.createProductTemplateSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('orders.productTemplates.service.createProductTemplateError'))
            throw error
        }
    }

    async updateProductTemplate(productTemplateId, payload) {
        try {
            const response = await productTemplatesQueries.updateProductTemplate(productTemplateId, payload)
            notificationService.success(t('orders.productTemplates.service.updateProductTemplateSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('orders.productTemplates.service.updateProductTemplateError'))
            throw error
        }
    }

    async deleteProductTemplate(productTemplateId) {
        try {
            const response = await productTemplatesQueries.deleteProductTemplate(productTemplateId)
            notificationService.success(t('orders.productTemplates.service.deleteProductTemplateSuccess'))

            return response
        } catch (error) {
            handleApiError(error, t('orders.productTemplates.service.deleteProductTemplateError'))
            throw error
        }
    }

    async fetchProductionFileInPaths() {
        try {
            const response = await productTemplatesQueries.fetchProductionFileInPaths()
            this.store.setProductionFileInPaths(response?.data?.results || [])
            return response
        } catch (error) {
            handleApiError(error, t('orders.productTemplates.service.fetchProductionFileInPathsError'))
            throw error
        }
    }

    async fetchFoldingTypes() {
        try {
            const response = await productTemplatesQueries.fetchFoldingTypes()
            this.store.setFoldingTypes(response?.data?.results || [])
            return response
        } catch (error) {
            handleApiError(error, t('orders.productTemplates.service.fetchFoldingTypesError'))
            throw error
        }
    }

    buildFiltersParams(filters = {}) {
        const params = {}

        if (filters.name) {
            params.name = filters.name
        }

        return params
    }

    setProductTemplatesFilters(filters) {
        this.store.setProductTemplatesFilters(filters)
    }

    resetProductTemplatesFilters() {
        this.store.resetProductTemplatesFilters()
    }
}

export const useProductTemplatesService = (options) => new ProductTemplatesService(options)
